import Head from 'next/head';
import { NextSeo } from 'next-seo';
import { Content } from '@/screens/Content';
import styled from 'styled-components';

//logos
import Logo from '@/public/images/logos/LoadsmartLogo.svg';

const responsive = `
    width: 100%;
    height: 100%;
`;

const LoadsmartLogo = styled(Logo)`
  ${responsive};
`;

//logos
import Link from 'next/link';

// data
const heroImages = {
  xxlarge: 'images/content/hero/hero-loadsmart-01-xxlg@2x.jpg',
  large: 'images/content/hero/hero-loadsmart-01-lg@2x.jpg',
  medium: 'images/content/hero/hero-loadsmart-01-md@2x.jpg',
  small: 'images/content/hero/hero-loadsmart-01-sm@2x.jpg',
};

const contentCopy = {
  date: `March 30, 2023`,
  title: `What is Truck Factoring?`,
  slug: `what-is-truck-factoring`,
  highlight: (
    <>
      Getting paid in the trucking industry can be frustrating. Depending on
      your contract with the shipper or broker, you may have to wait up to 90
      days after delivering a load to receive payment. Meanwhile, you need to
      keep your trucks rolling, which means fronting the money to haul your next
      load. This payment gap can be extremely tough for owner-operators and
      small fleet owners, who may have trouble with cash flow. But it also
      affects large fleet owners, who may have hundreds of outstanding invoices
      at any given time.
    </>
  ),
  copy: (
    <>
      <p>
        Factoring offers a solution to this dilemma. You simply create an
        invoice as normal, but instead of invoicing your customer directly, you
        send the invoice to a factoring company for a small fee. You get paid
        within minutes or hours, depending on the specifics of your factoring
        agreement, and the factoring company collects payment from your
        customer. Loadsmart’s CarrierGuide is a full-service truck management
        system (TMS) that offers factoring as an integration.
      </p>
      <nav>
        <ul>
          <li>
            <a href="#benefits-of-factoring">Benefits of factoring</a>
          </li>
          <li>
            <a href="#recourse-vs-non-recourse-factoring">
              Recourse vs. non-recourse factoring
            </a>
          </li>
          <li>
            <a href="#other-things-to-consider-in-a-factoring-agreement">
              Other things to consider in a factoring agreement
            </a>
          </li>
          <li>
            <a href="#putting-it-all-together">Putting it all together</a>
          </li>
        </ul>
      </nav>
      <h3 id="benefits-of-factoring">Benefits of factoring</h3>
      <p>
        Factoring offers several benefits for trucking companies of all sizes.
        These include:
        <ul>
          <li>
            <strong>Immediate payment.</strong> You’ll get your money in minutes
            to hours instead of weeks or months.
          </li>
          <li>
            <strong>Healthier cash flow.</strong> Since you aren’t waiting on
            payment for each load, you’ll have the cash you need to pay your
            bills and make your next delivery.
          </li>
          <li>
            <strong>Time and labor-saving.</strong> The factoring company
            collects on the invoices, which means you don’t need to spend time
            and labor on collections efforts.
          </li>
        </ul>
      </p>

      <h3 id="recourse-vs-non-recourse-factoring">
        Recourse vs. non-recourse factoring
      </h3>
      <p>
        One of the biggest points of confusion for many trucking companies is
        the difference between recourse and non-recourse factoring. But it’s
        important to understand because it affects what happens if the customer
        doesn’t pay.
      </p>
      <p>
        In recourse factoring, you retain the risk of non-payment. You will sell
        your invoice to the factoring company and receive your money upfront.
        But if the factoring service is unable to collect from your customer,
        you will have to buy back the invoice and pursue further collections
        efforts yourself. Exactly how much effort the factoring company will put
        into collections before requiring you to buy back the invoice varies, so
        be sure to read the fine print in your agreement.
      </p>
      <p>
        Recourse factoring is often less expensive than non-recourse factoring
        since you retain the risk. In addition, it is usually available for all
        your invoices, even if your customer has a questionable payment history.
        But it can be difficult for smaller trucking companies to come up with
        the funds to buy back invoices, so it is best for larger carriers who
        can afford that risk.
      </p>
      <p>
        In non-recourse factoring, the factoring service assumes the risk of
        nonpayment. To use non-recourse factoring, you will generally need to
        work with a freight broker that has been qualified and approved by the
        factoring company. You’ll sell your invoice to the factoring company,
        which will take on all efforts to collect from the client.
      </p>
      <p>
        However, not all non-recourse factoring agreements are the same. Many
        factoring services have limitations on when they will cover the
        non-payment. Some cover the invoice only when your client goes out of
        business or declares bankruptcy, while others will cover nearly all
        instances of bad debt. Read your agreement carefully to learn exactly
        which situations are covered.
      </p>

      <h3 id="other-things-to-consider-in-a-factoring-agreement">
        Other things to consider in a factoring agreement
      </h3>
      <p>
        A factoring agreement is a legal contract, so you will want to read all
        the fine print. In particular, take a look at the fees section and the
        terms and conditions.
      </p>
      <p>
        The factoring commission is the fee you’ll pay on each invoice for using
        the service. It’s generally a percentage of the invoice amount, though
        there may be a monthly minimum. Other possible fees include an
        origination fee for starting the contract, monthly maintenance fees, and
        an early termination fee.
      </p>
      <p>
        The terms and conditions spell out the details of your rights and
        obligations under the factoring agreement. Look for limitations on the
        total dollar amount and the per-client amount you can have in
        outstanding invoices, as well as specific instructions on how to create
        and submit your invoices. You’ll also need to find out whether you can
        pick and choose which of your accounts to factor. Finally, you will
        likely need to affirm that yours is a legitimate trucking business with
        operating authority and that your invoices are free of liens or other
        claims.
      </p>

      <h3 id="putting-it-all-together">Putting it all together</h3>
      <p>
        The trucking industry runs on delayed payments, so it can be difficult
        to pay for your next load when you are still waiting to get paid for the
        previous one. Factoring can be an excellent way to solve this issue and
        build a healthier cash flow. But you’ll want to read the factoring
        agreement carefully to be sure you understand your rights and
        responsibilities.
      </p>
      <p>
        Want to learn more about how Loadsmart’s CarrierGuide can revolutionize
        your back-office processes?&nbsp;
        <Link href="/carrier/tms/">
          Sign up for our FREE CarrierGuide today
        </Link>
        !
      </p>
    </>
  ),
};

const sidebarLinks = [
  {
    label:
      'Common Accounting Mistakes Trucking Companies Often Make And How To Avoid Them.',
    url: 'https://blog.loadsmart.com/2022/11/08/common-accounting-mistakes-trucking-companies-often-make-and-how-to-avoid-them',
    external: true,
  },
  {
    label:
      'Where’s My Money? Understanding the Different Ways to Pay Your Drivers.',
    url: 'https://blog.loadsmart.com/2022/11/04/wheres-my-money-understanding-the-different-ways-to-pay-your-carriers',
    external: true,
  },
  {
    label: 'How to Make More Money as a Carrier.',
    url: 'https://blog.loadsmart.com/2022/11/04/how-to-make-more-money-as-a-carrier',
    external: true,
  },
];

const bannerContent = {
  logo: <LoadsmartLogo />,
  title: (
    <>
      Get paid the same-day, improve cash flow, and simplify billing on ALL your
      loads with Loadsmart’s 2.5% factoring.
    </>
  ),
  bullets: [
    <>
      <strong>It’s Fast:</strong> Get paid on invoices same-day
    </>,
    <>
      <strong>It’s Simple:</strong> Build a load with Loadsmart
      <br /> and send invoices in one click
    </>,
    <>
      <strong>No Hidden Fees:</strong> The lowest factoring rate
      <br /> with NO additional charges
    </>,
    <>
      <strong>Live Customer Support:</strong> Helping ensure
      <br /> you get paid fast &amp; stress free
    </>,
  ],
  image: {
    small: '/images/content/carrierbanner/item-01-xxlarge.png',
    medium: '/images/content/carrierbanner/item-01-lg.png',
    large: '/images/content/carrierbanner/item-01-lg.png',
    xxlarge: '/images/content/carrierbanner/item-01-xxlarge.png',
  },
  cta: 'Learn more',
  url: 'https://lp.loadsmart.com/factoring-sign-up',
  alt: 'CarrierGuide dashboard screeenshots',
};

export default function WhatIsPartialTruckloadShipping() {
  return (
    <>
      <Head>
        <meta
          type="keywords"
          content="Freight factoring, Invoice factoring for trucking companies, Transportation factoring services, Trucking invoice financing, Cash flow solutions for trucking companies, Accounts receivable financing for truckers, Funding for transportation companies, Quick pay financing for trucking, Non-recourse factoring for trucking companies, Factoring for owner-operators"
        />
      </Head>
      <NextSeo
        nofollow={process.env.NOINDEX_FLAG === 'true'}
        noindex={process.env.NOINDEX_FLAG === 'true'}
        title="What is Factoring - Truck Factoring | Loadsmart"
        description="Freight factoring is a financing option for trucking companies that need immediate cash flow. Learn how it can benefit your freight operations."
        canonical="https://loadsmart.com/content/what-is-truck-factoring/"
        openGraph={{
          url: 'https://loadsmart.com/content/what-is-truck-factoring/',
        }}
      />
      <Content
        hero={heroImages}
        content={contentCopy}
        sidebar={sidebarLinks}
        banner={bannerContent}
      />
    </>
  );
}
